import lodash from 'lodash'
import store from '@/store'
import { EmailTypes } from '@/utils/consts'
import { fullYearDateFormat, amPmTimeFormat } from '@/utils/regex'
import { getSameBuildingUsers } from '@/api/users/request'
import { getAllTenancyAccounts } from '@/api/tenancies/request'
import { getRegionEnvsByCode } from '@/api/settings/request'
import { hotbAttrTypes } from '@/utils/locale'
import { PermTypeMapKeys } from '@/utils/aclHelper'
import { displayI18nFullName } from '@/utils/fullName'
import * as userUtil from '@/api/users/util'
import i18n from '@/utils/i18n'
import dateUtil from '@/utils/dateUtil'
import userHelper from '@/utils/userHelper'
import { getHotbCompanyName, getHotbPremises } from '@/modules/hotb/utils'
import { SEND_TO_ENGINEER, SEND_EMAIL } from './consts'
import { getLink, buildEmail, splitEmails } from './util'

function getMasterAccountFullName (masterAccount) {
  return displayI18nFullName(
    lodash.get(masterAccount, 'TenancyAccount.contactFirstName', ''),
    lodash.get(masterAccount, 'TenancyAccount.contactLastName', ''),
  )
}

async function getHotbInfo (needHistory = false) {
  const currentHotb = store.getters['hotb/currentHotb']
  const hotbInfo = { pic: {}, approver: {}, creator: {} }

  hotbInfo.id = currentHotb.id
  hotbInfo.type = currentHotb.type
  hotbInfo.premises = getHotbPremises(currentHotb)
  hotbInfo.transactionDate = currentHotb.transactionDate

  hotbInfo.tenancyId = lodash.get(currentHotb, 'tenancy.id')
  hotbInfo.buildingFullName = lodash.get(currentHotb, 'tenancy.building.fullName')
  const tenancyType = lodash.get(currentHotb, 'tenancy.type')
  hotbInfo.tenancyType = i18n.t(`tenancy.${lodash.toLower(tenancyType)}`)
  hotbInfo.tenancyName = getHotbCompanyName(currentHotb)

  hotbInfo.remarkToTenant = currentHotb.remarkToTenant
  if (needHistory) {
    hotbInfo.histories = lodash.cloneDeep(lodash.get(currentHotb, 'histories'))
  }

  if (currentHotb.manager.id) {
    hotbInfo.pic = {
      email: userUtil.getPrimaryEmail(currentHotb.manager),
      name: userHelper.getUserName(deep(currentHotb, 'manager.username'), deep(currentHotb, 'manager.displayName')),
    }
  }

  if (currentHotb.approver.id) {
    hotbInfo.approver = {
      email: userUtil.getPrimaryEmail(currentHotb.approver),
      name: userHelper.getUserName(deep(currentHotb, 'approver.username'), deep(currentHotb, 'approver.displayName')),
    }
  }

  if (currentHotb.scheduledTransactionDate) {
    hotbInfo.scheduledTransactionDate = currentHotb.scheduledTransactionDate
  }

  const tenantContact = { name: '', emails: [] }

  currentHotb.contacts.map(item => {
    if (item.type === 'NAME') {
      tenantContact.name = displayI18nFullName(item.value.firstName, item.value.lastName)
      tenantContact.firstName = item.value.firstName
      tenantContact.lastName = item.value.lastName
    } else if (item.type === 'EMAIL') {
      tenantContact.emails.push(item.value)
    }
  })
  hotbInfo.tenantContact = tenantContact
  const engineerEntry = store.getters['settings/engineerEntry']
  const vendorEntry = store.getters['settings/vendorEntry']
  const hotbLink = getLink('handover', hotbInfo.id, engineerEntry)
  const hotbTenantLink = getLink('tenantHandover', hotbInfo.id, vendorEntry)
  return [hotbInfo, hotbLink, hotbTenantLink]
}

async function getSameBuildingUsersEmails (perms) {
  const hotb = store.getters['hotb/currentHotb']
  const tenancyType = deep(hotb, 'tenancy.type')
  const buidlingId = deep(hotb, 'tenancy.building.id')
  const sameBuildingUsers = await getSameBuildingUsers(buidlingId, tenancyType, true, perms)
  const emails = sameBuildingUsers.map(item => item.email || item.username) || []
  return emails || []
}

async function buildEmailToTenant (extMetaData, actionType, specificTo = [], specificCC = [], specificBCC = []) {
  const emails = []
  const currentUser = store.getters['user/currentUser'] || {}
  const actionUser = {
    name: currentUser.name,
    email: userUtil.getPrimaryEmail(currentUser),
  }
  let [hotbInfo, hotbLink, hotbTenantLink] = await getHotbInfo()
  const scheduledTransDate = dateUtil.format(hotbInfo.scheduledTransactionDate, fullYearDateFormat)
  const scheduledTransTime = dateUtil.format(hotbInfo.scheduledTransactionDate, amPmTimeFormat)
  hotbInfo = {
    ...hotbInfo,
    ...extMetaData,
    actionUser,
    scheduledTransDate,
    scheduledTransTime,
  }
  const ccEmails = new Set(specificCC)
  const bccEmails = new Set(specificBCC)
  bccEmails.add(hotbInfo.approver.email)
  const meteData = {
    hotbInfo,
    hotbLink,
    hotbTenantLink,
    cc: ccEmails,
    bcc: bccEmails,
    docIds: hotbInfo.docIds || [],
  }

  let sendTo = new Set(specificTo)
  hotbInfo.tenantContact.emails.forEach(item => { sendTo.add(item) })

  if (sendTo && actionType) {
    const template = EmailTypes[hotbAttrTypes[hotbInfo.type]][actionType]
    buildEmail(emails, sendTo, template, meteData, SEND_EMAIL)
  }
  return emails
}

const hotbNotification = {
  writeSubmitForApprovalEmail: async () => {
    const emails = []
    const [hotbInfo, hotbLink, hotbTenantLink] = await getHotbInfo()

    const meteData = {
      hotbInfo,
      hotbTenantLink,
      hotbLink,
    }

    if (deep(hotbInfo, 'approver.email')) {
      const template = EmailTypes[hotbAttrTypes[hotbInfo.type]].submitForApproval
      buildEmail(emails, hotbInfo.approver.email, template, meteData, SEND_EMAIL)
    }
    return emails
  },

  writeArrangeEmail: async (extMetaData = {}) => {
    const currentHotb = store.getters['hotb/currentHotb']
    const tenancyAccounts = await getAllTenancyAccounts(deep(currentHotb, 'tenancy.id'))
    const masterAccount = tenancyAccounts.filter(account => account.TenancyAccount.isAdmin)[0] || {}
    const masterAccountFullName = getMasterAccountFullName(masterAccount)
    const masterAccountEmail = masterAccount.username
    const additionalEmails = splitEmails(lodash.get(currentHotb, 'additionalEmails', []))
    extMetaData = {
      ...extMetaData,
      masterAccount: { name: masterAccountFullName, email: masterAccountEmail },
    }
    const specificTo = [masterAccountEmail]

    const rolesEmails = await getSameBuildingUsersEmails(PermTypeMapKeys.HOTB_ARRANGE_APPROVE_UPDATE_COMPLETE)

    const currentUserEmail = store.getters['user/currentUserEmail']
    let bcc = [...rolesEmails]
    // exclude the actioner
    bcc = bcc.filter(email => email !== currentUserEmail)

    const emails = await buildEmailToTenant(extMetaData, 'arrange', specificTo, [currentUserEmail, ...additionalEmails], bcc)
    return emails
  },

  writeNotifyUnsignedEmail: async (extMetaData) => {
    const currentHotb = store.getters['hotb/currentHotb']
    const completedDate = dateUtil.format(currentHotb.completedDate, fullYearDateFormat)
    const completedTime = dateUtil.format(currentHotb.completedDate, amPmTimeFormat)
    extMetaData = {
      ...extMetaData,
      completedDate,
      completedTime,
    }

    const rolesEmails = await getSameBuildingUsersEmails(PermTypeMapKeys.HOTB_UPDATE_COMPLETE)

    const currentUserEmail = store.getters['user/currentUserEmail']
    let bcc = [...rolesEmails]
    // exclude the actioner
    bcc = bcc.filter(email => email !== currentUserEmail)

    const emails = await buildEmailToTenant(extMetaData, 'unsigned', [], [currentUserEmail], bcc)
    return emails
  },

  writeCompleteEmail: async (extMetaData) => {
    const currentHotb = store.getters['hotb/currentHotb']
    const completedDate = dateUtil.format(currentHotb.completedDate, fullYearDateFormat)
    const completedTime = dateUtil.format(currentHotb.completedDate, amPmTimeFormat)
    extMetaData = {
      ...extMetaData,
      completedDate,
      completedTime,
    }

    const rolesEmails = await getSameBuildingUsersEmails(PermTypeMapKeys.HOTB_UPCOM_CCD_TENANT_CONTACT_CRUP)

    const envMailArray = ['mncGenerals', 'cscGenerals', 'leaseAdminGenerals']
    const envMails = []
    await Promise.all(envMailArray.map(async envName => {
      const emailString = await getRegionEnvsByCode(`mail.${envName}`)
      if (emailString) {
        emailString.split(';').forEach(subStr => envMails.push(subStr.trim()))
      }
    }))

    const currentUserEmail = store.getters['user/currentUserEmail']
    let bcc = [...rolesEmails, ...envMails]
    // exclude the actioner
    bcc = bcc.filter(email => email !== currentUserEmail)

    const emails = await buildEmailToTenant(extMetaData, 'complete', [], [currentUserEmail], bcc)
    return emails
  },

  writeRequestChangeEmail: async (extMetaData) => {
    const emails = []
    const [hotbInfo, hotbLink, hotbTenantLink] = await getHotbInfo()
    const meteData = {
      hotbInfo,
      hotbLink,
      hotbTenantLink,
      comments: extMetaData.comments,
    }

    let rolePMSECEmails = await getSameBuildingUsersEmails(PermTypeMapKeys.HOTB_ARRANGE)
    const to = new Set(rolePMSECEmails)

    const rolesEmails = await getSameBuildingUsersEmails(PermTypeMapKeys.HOTB_ARRANGE_UPDATE_COMPLETE)
    const bcc = [...rolesEmails]
    bcc.push(hotbInfo.approver.email)
    meteData.bcc = new Set(bcc)

    if (to) {
      const template = EmailTypes[hotbAttrTypes[hotbInfo.type]].requestChange
      buildEmail(emails, to, template, meteData, SEND_EMAIL)
    }
    return emails
  },
  writeResetToDraftEmail: async (extMetaData) => {
    const emails = []
    const currentHotb = store.getters['hotb/currentHotb']
    const hotbLink = getLink('handovers', currentHotb.id)
    const hotbInfo = await getHotbInfo()
    if (hotbInfo.ccEmails.indexOf(hotbInfo.pic.email) > -1) {
      hotbInfo.ccEmails.splice(hotbInfo.ccEmails.indexOf(hotbInfo.pic.email), 1)
    }
    if (hotbInfo.ccEmails.indexOf(hotbInfo.approver.email) <= -1) {
      hotbInfo.ccEmails.push(hotbInfo.approver.email)
    }
    const cc = hotbInfo.ccEmails
    const meteData = {
      hotbInfo,
      hotbLink,
      cc,
    }

    if (deep(hotbInfo, 'pic.email')) {
      const template = EmailTypes[hotbAttrTypes[currentHotb.type]].resetToDraft
      buildEmail(emails, hotbInfo.pic.email, template, meteData, SEND_TO_ENGINEER)
    }
    return emails
  },
}

export default hotbNotification
